import { IResourceComponentsProps, useShow } from "@refinedev/core";
import { Show, NumberField, TextField } from "@refinedev/mantine";
import {
  Title,
  Badge,
  Grid,
  Flex,
  Anchor,
  ThemeIcon,
  Box,
  Card,
  Group,
} from "@mantine/core";
import { CoinEarning } from "entities/coin_earning";
import { DetailsPair } from "components/common/show/DetailsPair";
import { formatDistanceToNow } from "date-fns";
import { History } from "components/common/show/History";
import { DetailsUser } from "components/common/show/DetailsUser";
import { Text } from "@mantine/core";
import { colorsByState } from "entities/coin_earning/state";
import { colorsBySource } from "entities/coin_earning/source";
import { getInvoiceUrl } from "utils/chargebee";
import { colorsByLockType } from "entities/coin_earning/lock";
import { IconLock, IconLockOpen } from "@tabler/icons";

export const CoinEarningShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<CoinEarning>();
  const { data, isLoading } = queryResult;

  const coinEarning = data?.data;

  return (
    <Show isLoading={isLoading}>
      <Grid>
        <Grid.Col span={8}>
          {Object.values(coinEarning?.locks || {}).length !== 0 && (
            <Flex direction="column" gap="sm" p="lg">
              <h2>Locks</h2>
              {coinEarning &&
                Object.values(coinEarning.locks).map((lock) => {
                  const isLocked = !lock.released_at;

                  return (
                    <Card
                      key={lock.lock_type}
                      shadow="sm"
                      p="md"
                      radius="md"
                      withBorder
                    >
                      <Group position="apart" mt="xs" mb="xs">
                        {lock.lock_type && (
                          <>
                            <Flex align="center">
                              <Text weight="bold" mr="xs">
                                {lock.lock_type
                                  .toUpperCase()
                                  .replaceAll("_", " ")}
                              </Text>
                            </Flex>
                            <Flex align="center">
                              {isLocked ? (
                                <>
                                  <Text size="lg" mr="md" color="red" italic>
                                    Locked
                                  </Text>

                                  <ThemeIcon
                                    color="red"
                                    size="xl"
                                    variant="light"
                                  >
                                    <IconLock />
                                  </ThemeIcon>
                                </>
                              ) : (
                                <>
                                  <Text size="lg" mr="md" color="blue" italic>
                                    Unlocked
                                  </Text>
                                  <ThemeIcon
                                    color="blue"
                                    size="xl"
                                    variant="light"
                                  >
                                    <IconLockOpen />
                                  </ThemeIcon>
                                </>
                              )}
                            </Flex>
                          </>
                        )}
                      </Group>

                      {!isLocked && (
                        <Flex direction="column" gap="sm">
                          <DetailsPair>
                            <Title order={5}>Released At</Title>
                            {lock?.released_at && (
                              <TextField
                                value={formatDistanceToNow(
                                  new Date(lock.released_at)
                                )}
                              />
                            )}
                          </DetailsPair>
                          <DetailsPair>
                            <Title order={5}>Released Notes</Title>
                            <Text size="sm" color="dimmed">
                              {lock?.released_notes}
                            </Text>
                          </DetailsPair>
                        </Flex>
                      )}
                    </Card>
                  );
                })}
            </Flex>
          )}
          {coinEarning && (
            <History<CoinEarning>
              resources={"coin_earnings"}
              entityId={coinEarning.id}
            />
          )}
        </Grid.Col>
        <Grid.Col span={4}>
          <Flex direction="column" gap="sm">
            <DetailsPair>
              <Title order={5}>User</Title>
              {coinEarning?.user && <DetailsUser user={coinEarning.user} />}
            </DetailsPair>
            <DetailsPair>
              <Title my="xs" order={5}>
                Source
              </Title>
              {coinEarning && (
                <Badge color={colorsBySource[coinEarning.source]}>
                  {coinEarning.source.replaceAll("_", " ")}
                </Badge>
              )}
            </DetailsPair>
            <DetailsPair>
              <Title my="xs" order={5}>
                State
              </Title>
              {coinEarning && (
                <Badge color={colorsByState[coinEarning.state]}>
                  {coinEarning.state}
                </Badge>
              )}
            </DetailsPair>
            <DetailsPair>
              <Title my="xs" order={5}>
                Amount
              </Title>
              <Text color="green" weight="bold">
                {coinEarning?.amount}
              </Text>
            </DetailsPair>
            <DetailsPair>
              <Title my="xs" order={5}>
                Created At
              </Title>
              {coinEarning?.created_at && (
                <TextField
                  value={formatDistanceToNow(new Date(coinEarning.created_at))}
                />
              )}
            </DetailsPair>
            <DetailsPair>
              <Title my="xs" order={5}>
                Updated At
              </Title>
              {coinEarning?.updated_at && (
                <TextField
                  value={formatDistanceToNow(new Date(coinEarning.updated_at))}
                />
              )}
            </DetailsPair>
            <DetailsPair>
              <Title my="xs" order={5}>
                Remote Invoice
              </Title>
              {coinEarning && coinEarning.remote_invoice_id && (
                <Anchor
                  href={getInvoiceUrl(coinEarning.remote_invoice_id)}
                  target="_blank"
                >
                  {coinEarning.remote_invoice_id}
                </Anchor>
              )}
            </DetailsPair>
            <DetailsPair>
              <Title my="xs" order={5}>
                Id
              </Title>
              {coinEarning && <NumberField value={coinEarning?.id} />}
            </DetailsPair>
            <DetailsPair>
              <Title my="xs" order={5}>
                Notes
              </Title>
              {coinEarning && <TextField value={coinEarning.notes} />}
            </DetailsPair>
          </Flex>
        </Grid.Col>
      </Grid>
    </Show>
  );
};
